/**
 * Which Christmas and New Year overrides the Build page sends with a new
 * schedule. Pure, so the rule is tested without a browser.
 *
 * The admin confirms the special weeks before generating (CLAUDE.md §11):
 * the rule's pick is offered, and either week can be moved or set to none.
 * An override is stored per week and shows on the schedule as "Special week
 * set by admin", so one must be sent only for a week whose kind the admin
 * actually changed. Accepting the rule's pick sends nothing at all;
 * otherwise every Christmas and New Year week would claim to have been set
 * by hand.
 */
import type { ISODate } from '../dates/iso.ts';
import type { SpecialOverride } from '../weeks/weeks.ts';

/** The rule's Christmas week and the week after it, for one Christmas in the period. */
export interface SpecialPair {
  christmas: ISODate;
  newYear: ISODate;
}

/** The admin's confirmation for one pair: the weeks they named, or null for none. */
export interface SpecialPick {
  christmas: ISODate | null;
  newYear: ISODate | null;
}

/**
 * The overrides for every pair, keyed by the week's Monday. `chosen` is keyed
 * by the rule's Christmas week; a pair with no entry is as the rule says.
 */
export function specialOverrides(pairs: readonly SpecialPair[], chosen: Readonly<Record<ISODate, SpecialPick>>): Record<ISODate, SpecialOverride> {
  const out: Record<ISODate, SpecialOverride> = {};
  for (const pair of pairs) {
    const pick = chosen[pair.christmas] ?? pair;
    // What the admin wants each affected week to be. The rule's two weeks
    // become regular unless picked again; the picked weeks take their kind.
    const wanted = new Map<ISODate, SpecialOverride>([
      [pair.christmas, 'regular'],
      [pair.newYear, 'regular'],
    ]);
    if (pick.christmas) wanted.set(pick.christmas, 'christmas');
    if (pick.newYear) wanted.set(pick.newYear, 'new-year');
    for (const [week, kind] of wanted) {
      const byRule: SpecialOverride = week === pair.christmas ? 'christmas' : week === pair.newYear ? 'new-year' : 'regular';
      if (kind !== byRule) out[week] = kind;
    }
  }
  return out;
}
